import { useEffect } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'

function DisconnectAccountModal({
  isOpen,
  onClose,
  onConfirm,
  account,
  scheduledStoryCount = 0
}) {
  const { t } = useTranslation('settings')

  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen, onClose])

  if (!isOpen || !account) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        className="relative bg-white rounded-xl shadow-xl max-w-md w-full mx-auto max-h-[90vh] overflow-y-auto animate-in zoom-in-95 duration-200"
        role="dialog"
        aria-labelledby="disconnect-modal-title"
        aria-describedby="disconnect-modal-description"
      >
        <div className="p-6">
          {/* Icon */}
          <div className="mx-auto flex items-center justify-center h-14 w-14 rounded-full bg-yellow-100 mb-4">
            <ExclamationTriangleIcon className="h-7 w-7 text-yellow-600" aria-hidden="true" />
          </div>

          {/* Title */}
          <h3
            id="disconnect-modal-title"
            className="text-lg font-semibold text-gray-900 text-center mb-2"
          >
            {t('accounts.disconnect.modal.title')}
          </h3>

          {/* Account info */}
          <div className="flex items-center justify-center space-x-3 mb-4">
            {account.profile_picture_url && (
              <img
                src={account.profile_picture_url}
                alt={account.username}
                className="w-10 h-10 rounded-full"
              />
            )}
            <span className="font-medium text-gray-900">@{account.username}</span>
          </div>

          {/* Description */}
          <div id="disconnect-modal-description" className="space-y-3">
            <p className="text-sm text-gray-600 text-center">
              {t('accounts.disconnect.modal.description')}
            </p>

            {/* What happens list */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm font-medium text-yellow-900 mb-2">
                {t('accounts.disconnect.modal.whatHappens')}
              </p>
              <ul className="space-y-1 text-sm text-yellow-800">
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>{t('accounts.disconnect.modal.effects.dataKept')}</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>{t('accounts.disconnect.modal.effects.canReconnect')}</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>{t('accounts.disconnect.modal.effects.billingAccess')}</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>{t('accounts.disconnect.modal.effects.noNewContent')}</span>
                </li>
              </ul>
            </div>

            {/* Scheduled stories warning */}
            {scheduledStoryCount > 0 && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <p className="text-sm text-orange-800">
                  <span className="font-medium">{t('accounts.disconnect.modal.scheduledWarning.title')}</span>
                  {' '}
                  {t('accounts.disconnect.modal.scheduledWarning.message', { count: scheduledStoryCount })}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end space-x-3 px-6 py-4 bg-gray-50 rounded-b-xl">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-100 border border-gray-300 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
          >
            {t('accounts.disconnect.modal.button.cancel')}
          </button>
          <button
            onClick={() => {
              onConfirm()
              onClose()
            }}
            className="px-4 py-2 text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
          >
            {t('accounts.disconnect.modal.button.confirm')}
          </button>
        </div>
      </div>
    </div>
  )
}

DisconnectAccountModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  account: PropTypes.shape({
    id: PropTypes.string.isRequired,
    username: PropTypes.string.isRequired,
    profile_picture_url: PropTypes.string
  }),
  scheduledStoryCount: PropTypes.number
}

export default DisconnectAccountModal